import { Box, Typography } from '@mui/material';
import { Button } from '@nps.today/component-library';
import { FunctionComponent, ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslator, translator } from 'translation/useTranslator';

export const getDropzoneError = (error: string) => {
  return (
    translator?.dropZoneError(error) ??
    `${error} Please check that the file is in the right format and is not open in any other application.`
  );
};

export type DropzoneStatus =
  | 'added'
  | 'done'
  | 'removed'
  | 'error'
  | 'error_file_size'
  | 'error_file_type';

export type DropzoneFile = {
  file: File;
  meta: {
    name: string;
    size: number;
    type: string;
    [key: string]: any;
  };
  remove: () => void;
};

export type CustomDropzoneProps = {
  accept?: string;
  maxFiles?: number;
  maxSizeBytes?: number;
  disabled?: boolean;
  inputContent?: ReactNode;
  onChangeStatus?: (file: DropzoneFile, status: DropzoneStatus) => void;
  validate?: (file: DropzoneFile | null) => Promise<boolean | void> | boolean | void;
};

const isFileAccepted = (file: File, accept?: string) => {
  if (!accept) return true;
  const accepted = accept
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);

  return accepted.some((entry) => {
    if (entry.endsWith('/*')) {
      const prefix = entry.replace('/*', '');
      return file.type.startsWith(prefix);
    }
    if (entry.startsWith('.')) {
      return file.name.toLowerCase().endsWith(entry.toLowerCase());
    }
    return file.type === entry;
  });
};

const CustomDropzone: FunctionComponent<CustomDropzoneProps> = ({
  accept,
  maxFiles = 1,
  maxSizeBytes,
  disabled,
  inputContent,
  onChangeStatus,
  validate
}) => {
  const translator = useTranslator();
  const [currentFile, setCurrentFile] = useState<DropzoneFile | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const clearFile = useCallback(
    (file?: DropzoneFile) => {
      if (file) {
        onChangeStatus?.(file, 'removed');
      }
      setCurrentFile(null);
    },
    [onChangeStatus]
  );

  const createDropzoneFile = useCallback(
    (file: File): DropzoneFile => {
      const dropzoneFile: DropzoneFile = {
        file,
        meta: {
          name: file.name,
          size: file.size,
          type: file.type
        },
        remove: () => clearFile(dropzoneFile)
      };
      return dropzoneFile;
    },
    [clearFile]
  );

  const handleFileSelect = useCallback(
    async (file: File | null) => {
      if (!file) {
        clearFile(currentFile ?? undefined);
        validate?.(null);
        return;
      }

      if (disabled) return;
      if (maxFiles <= 1 && currentFile) {
        clearFile(currentFile);
      }

      const dropzoneFile = createDropzoneFile(file);
      onChangeStatus?.(dropzoneFile, 'added');

      if (!isFileAccepted(file, accept)) {
        onChangeStatus?.(dropzoneFile, 'error_file_type');
        return;
      }

      if (maxSizeBytes && file.size > maxSizeBytes) {
        onChangeStatus?.(dropzoneFile, 'error_file_size');
        return;
      }

      try {
        const validationResult = await validate?.(dropzoneFile);
        if (validationResult === false) throw new Error('validation');
        setCurrentFile(dropzoneFile);
        onChangeStatus?.(dropzoneFile, 'done');
      } catch {
        setCurrentFile(null);
        onChangeStatus?.(dropzoneFile, 'error');
      }
    },
    [
      accept,
      clearFile,
      createDropzoneFile,
      currentFile,
      disabled,
      maxFiles,
      maxSizeBytes,
      onChangeStatus,
      validate
    ]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    handleFileSelect(selectedFile);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const droppedFile = event.dataTransfer.files?.[0] ?? null;
    handleFileSelect(droppedFile);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  return (
    <Box
      onClick={() => (disabled ? undefined : inputRef.current?.click())}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={() => setIsDragging(true)}
      onDragLeave={() => setIsDragging(false)}
      sx={(theme) => ({
        height: 'min-content',
        minHeight: '100px',
        borderRadius: 1,
        border: '2px dashed',
        borderColor: isDragging ? theme.palette.primary.main : theme.palette.divider,
        p: 2,
        textAlign: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        backgroundColor: isDragging ? theme.palette.action.hover : 'transparent'
      })}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        disabled={disabled}
        style={{ display: 'none' }}
      />
      {currentFile ? (
        <Typography color="primary">{currentFile.meta.name}</Typography>
      ) : (
        inputContent || (
          <Typography key="dropzoneInputContent">
            {translator.dragFilesOrClickToBrowse()}
          </Typography>
        )
      )}
      {currentFile && (
        <Button
          dataTestId="remove-file"
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            clearFile(currentFile);
          }}
        >
          {translator.delete()}
        </Button>
      )}
    </Box>
  );
};

export default CustomDropzone;
